import { Card, Button, OverlayTrigger, Tooltip, Spinner, Dropdown, Form } from "react-bootstrap";
import { Recording } from "./interfaces/Recording";
import { formatDuration } from "../../utils/durationFormatter";
import { useState, useCallback, memo } from "react";
import {
  requestWithAuthorization,
} from "../../network/apiRequests";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFilePdf,
  faGears,
  faShapes,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";
import { MdContentCopy } from "react-icons/md";

interface RecordingCardProps {
  recording: Recording;
  userId?: string;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
}

const RecordingCard = memo(function RecordingCard({
  recording,
  userId,
  isSelectable = false,
  isSelected = false,
  onToggleSelect
}: RecordingCardProps) {
  const [isLoading, setIsLoading] = useState<string[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);

  
  const linkBuilder = useCallback((recordingId: string | null) => {
    if (recordingId === null) {
      return "#";
    }
    return `${process.env.REACT_APP_API_HOSTNAME}/download/${recordingId}?fileType=csv`;
  }, []);

  const handleReportDownloadClick = useCallback(async (
    reportId: string | null,
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    if (reportId === null) {
      return;
    }

    const loadingKey = `report-${recording.recordingId}`;
    setIsLoading((prevLoading) => [...prevLoading, loadingKey]);

    try {
      const url = `${
        process.env.REACT_APP_API_HOSTNAME
      }/report/download?reportId=${encodeURIComponent(reportId)}`;
      const response = await requestWithAuthorization(url, { responseType: "blob" });

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      var filename = "report.pdf";
      if (contentDisposition) {
        // Try different patterns for filename extraction
        let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
        }
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, '');
        }
      }

      // Use the content-type from the response if available
      const contentType = response.headers['content-type'] || "application/pdf";

      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      if (link.parentNode != null) {
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error("Error fetching report: ", error);
    } finally {
      setIsLoading((prevLoading) => prevLoading.filter((key) => key !== loadingKey));
    }
  }, [recording.recordingId]);

  const downloadRecording = useCallback(async (
    userId: string | undefined,
    recordingId: string | null,
    downloadType: 'raw' | 'combined' | 'processed' = 'raw'
  ) => {
    if (userId === undefined || recordingId === null) {
      return;
    }

    const loadingKey = `${downloadType}-${recording.recordingId}`;
    setIsLoading((prevLoading) => [...prevLoading, loadingKey]);

    let url = linkBuilder(recordingId);
    if (downloadType === 'combined') {
      url += '&combined=true';
    } else if (downloadType === 'processed') {
      url += '&processed=true';
    }

    try {
      const response = await requestWithAuthorization(url, { responseType: "blob" });
      if (response.status === 200) {
        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers['content-disposition'];
        var filename = `recording_${downloadType}.csv`;
        if (contentDisposition) {
          // Try different patterns for filename extraction
          let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
          if (!filenameMatch) {
            filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
          }
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, '');
          }
        }

        // Use the content-type from the response if available
        const contentType = response.headers['content-type'] || "application/csv";

        const blob = new Blob([response.data], {
          type: contentType,
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        // Clean up
        if (link.parentNode != null) {
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error("Error fetching recording: ", error);
    } finally {
      setIsLoading((prevLoading) => prevLoading.filter((key) => key !== loadingKey));
    }
  }, [linkBuilder]);

  const copyRecordingId = useCallback(async () => {
    if (recording.recordingId === null) {
      return;
    }
    try {
      await navigator.clipboard.writeText(recording.recordingId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy recording ID:', error);
    }
  }, [recording.recordingId]);

  const formattedDate = new Date(recording.timestamp).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const formattedDuration = formatDuration(recording.duration);

  return (
    <Card
      className={`h-100 recording-card ${isSelectable ? 'position-relative' : ''} ${isSelected ? 'border-primary shadow-sm' : ''}`}
      style={{
        backgroundColor: '#1a1d1f',
        borderColor: isSelected ? '#0d6efd' : '#495057',
        cursor: isSelectable ? 'pointer' : 'default',
        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
      }}
      onClick={() => {
        if (isSelectable && onToggleSelect) {
          onToggleSelect(recording.recordingId);
        }
      }}
    >
      {isSelectable && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 10
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect && onToggleSelect(recording.recordingId)}
            style={{ transform: 'scale(1.25)', cursor: 'pointer' }}
            id={`recording-checkbox-${recording.recordingId}`}
            aria-label={`Select recording ${recording.name}`}
          />
        </div>
      )}
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-2 pe-4">
          <Card.Title className="mb-0 text-white" style={{ fontSize: '1rem' }}>
            {recording.name}
          </Card.Title>
          <OverlayTrigger
            overlay={
              <Tooltip id={`copy-${recording.recordingId}`}>
                {copySuccess ? 'Copied!' : 'Copy Recording ID'}
              </Tooltip>
            }
          >
            <div
              onClick={copyRecordingId}
              style={{ cursor: 'pointer' }}
              className="text-light ms-2"
            >
              <MdContentCopy
                size={14}
                color={copySuccess ? '#28a745' : '#6c757d'}
              />
            </div>
          </OverlayTrigger>
        </div>

        <div className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
          <div className="mb-1">
            <strong>Date:</strong> {formattedDate}
          </div>
          <div className="mb-1">
            <strong>Duration:</strong> {formattedDuration}
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            {recording.reportExisting && (
              <OverlayTrigger
                overlay={<Tooltip id={`report-${recording.recordingId}`}>Download Report</Tooltip>}
              >
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={(e) => handleReportDownloadClick(recording.recordingId, e)}
                  disabled={isLoading.includes(`report-${recording.recordingId}`)}
                >
                  {isLoading.includes(`report-${recording.recordingId}`) ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faFilePdf} />
                  )}
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
});

export default RecordingCard;